import * as am4core from '@amcharts/amcharts4/core';
import {
    XYChart,
    LineSeries,
    ValueAxis,
    CircleBullet,
    IXYSeriesDataFields,
    Legend
} from '@amcharts/amcharts4/charts';

import { ChartModel } from '../chart.model';
import { chartColors } from '../../enums/chart-colors.enum';
import { LineChartOptionsModel } from './line-chart-options.model';
import { DataPointModel } from '../data-point.model';
import { AxisLineModel } from './axis-line.model';
import { LineSeriesModel } from './line-series.model';
import { isNumber } from 'underscore';


export class LineChartModel extends ChartModel {
    readonly singleSeries: boolean = false;

    declare amchart: XYChart;
    declare options: LineChartOptionsModel;
    declare series: LineSeriesModel[];

    seriesWithProjections: {dataLine: LineSeries; projectionLine: LineSeries}[] = [];

    /**
     * Build XY Chart with x-axis of dates and y-axis with some numeric values
     */
    public initChart(element: HTMLElement): void {
        this.element = element;
        this.amchart = am4core.create(this.element, XYChart);

        // Y-Axis contains numeric data
        const yAxis: ValueAxis = this.amchart.yAxes.push(new ValueAxis());
        yAxis.renderer.minGridDistance = 20;
        yAxis.extraMin = 0;
        yAxis.extraMax = 0;
        if (this.options.strictMinMax) {
            yAxis.strictMinMax = true;
        }
        if (this.options.yValueLabel) {
            yAxis.title.text = this.options.yValueLabel;
        }
        // Compared against null rather than truthiness, so a floor of 0 is honoured - without it an
        // all zero series is drawn with a -1 gridline under it
        if (this.options.minYValue !== undefined && this.options.minYValue !== null) {
            yAxis.min = this.options.minYValue;
            yAxis.adapter.add('min', minimum => Math.max(this.options.minYValue, minimum ?? this.options.minYValue));
        }
        if (this.options.maxYValue) {
            yAxis.max = this.options.maxYValue;
        }
        if (this.options.precision !== undefined || this.options.precision !== null) {
            yAxis.maxPrecision = this.options.precision;
        }
        if (this.options.numberFormat) {
            yAxis.numberFormatter.numberFormat = this.options.numberFormat;
        }

        if (this.options.horizontalLines && this.options.horizontalLines.length > 0) {
            this.options.horizontalLines.forEach((lineDef) => {
                this.addAxisLine(yAxis, lineDef, false);
            });
        }

        // X-Axis contains the time info (hours or days)
        const xAxis = this.amchart.xAxes.push(new ValueAxis());
        xAxis.extraMin = 0;
        xAxis.extraMax = 0;
        if (this.options.strictMinMax) {
            xAxis.strictMinMax = true;
        }
        if (this.options.minXValue) {
            xAxis.min = this.options.minXValue;
        }
        if (this.options.maxXValue) {
            xAxis.max = this.options.maxXValue;
        }
        // Callers whose labels are wider than a few characters ask for more room between them
        xAxis.renderer.minGridDistance = this.options.minGridDistance ?? 25;
        if (this.options.xValueLabel) {
            xAxis.title.text = this.options.xValueLabel;
        }
        if (this.options.precision !== undefined || this.options.precision !== null) {
            xAxis.maxPrecision = this.options.precision;
        }
        if (this.options.xLabelFunction) {
            xAxis.numberFormatter = new am4core.NumberFormatter();
            xAxis.numberFormatter.format = this.options.xLabelFunction;
        }
        // Enable under axis 'ticks'
        xAxis.renderer.ticks.template.disabled = false;
        xAxis.renderer.ticks.template.strokeOpacity = 1;
        xAxis.renderer.ticks.template.stroke = am4core.color(chartColors.agprLightGrey);
        xAxis.renderer.ticks.template.strokeWidth = 1;
        xAxis.renderer.ticks.template.length = 8;
        if (this.options.verticalLines && this.options.verticalLines.length > 0) {
            this.options.verticalLines.forEach((lineDef) => {
                this.addAxisLine(xAxis, lineDef, true);
            });
        }

        const colors = this.getSeriesColors();
        for (let i = 0; i < this.series.length; i++) {
            this.addSeries(
                i,
                colors[i]
            );
        }

        const legend = this.amchart.legend = new Legend();
        legend.contentAlign = 'left';
        legend.valign = 'bottom';
        legend.fontSize = '0.75rem';
        legend.paddingLeft = 10;
        legend.paddingTop = 8;
        legend.paddingBottom = 8;
        legend.labels.template.text = '{name}';
        // legend.itemContainers.template.interactionsEnabled = false;
        legend.itemContainers.template.paddingTop = 2;
        legend.itemContainers.template.paddingBottom = 2;

        if (isNumber(this.amchart.paddingRight)) {
            (this.amchart.paddingRight += 15);
        }
    }

    /**
     * Gets the colors for the chart series.
     * Prevents already used colors from being reused, including those used for axis lines
     */
    protected getSeriesColors(): string[] {
        const unusedColors = Object.values(this.defaultChartColors.generalColors);

        const seriesColors = [];

        // Remove availability of colors that are used as horizontal lines
        if (this.options.horizontalLines && this.options.horizontalLines.length > 0) {
            this.options.horizontalLines.forEach((lineDef) => {
                const color = lineDef.color as chartColors;
                const index = unusedColors.indexOf(color);
                if (index !== -1) {
                    unusedColors.splice(index, 1);
                }
            });
        }
        // Remove availability of colors that are used as vertical lines
        if (this.options.verticalLines && this.options.verticalLines.length > 0) {
            this.options.verticalLines.forEach((lineDef) => {
                const color = lineDef.color as chartColors;
                const index = unusedColors.indexOf(color);
                if (index !== -1) {
                    unusedColors.splice(index, 1);
                }
            });
        }
        // Remove availability of colors that are defined for specific series
        for (let i = 0, l = this.series.length; i < l; i++) {
            const color = this.series[i].color as chartColors;
            if (color) {
                seriesColors.push(color);
                const index = unusedColors.indexOf(color);
                if (index !== -1) {
                    unusedColors.splice(index, 1);
                }
            } else {
                seriesColors.push(undefined);
            }
        }
        // Fill in colors for series that don't have a color defined
        for (let i = 0, l = seriesColors.length; i < l; i++) {
            if (!seriesColors[i]) {
                seriesColors[i] = unusedColors.shift();
            }
        }

        return seriesColors;
    }

    /**
     * Adds a series to the chart
     */
    protected addSeries(seriesIndex: number, color: string) {
        const series = this.series[seriesIndex];
        const amseries: LineSeries = this.amchart.series.push(new LineSeries());
        let dataFields: IXYSeriesDataFields = amseries.dataFields;

        dataFields.valueY = 'yValue';
        dataFields.valueX = 'xValue';

        amseries.strokeWidth = 2;
        amseries.stroke = am4core.color(color);

        const bullet: CircleBullet = amseries.bullets.push(new CircleBullet());
        bullet.circle.strokeWidth = 1;
        bullet.circle.radius = 3;
        bullet.circle.fill = am4core.color('#fff');
        bullet.circle.stroke = am4core.color(color);
        const bulletHover = bullet.states.create('hover');
        bulletHover.properties.scale = 1.3;

        bullet.tooltip = new am4core.Tooltip();
        if (this.options.tooltipHTML) {
            bullet.tooltipHTML = this.options.tooltipHTML.replace('{color}', color);
            this.styleHtmlTooltip(bullet.tooltip);
        } else {
            bullet.tooltipText = '{label} - {yValue.formatNumber(\'#,###\')}';
            bullet.tooltip.fill = amseries.stroke;
            bullet.tooltip.getFillFromObject = false;
            bullet.tooltip.background.fill = amseries.stroke;
        }

        if (this.options.curved) {
            amseries.tensionX = 0.8;
            amseries.tensionY = 0.9;
        }

        if (seriesIndex !== null) {
            amseries.name = series.label;
            // TODO : Verify this doesn't break anything
            if (!series.label) {
                amseries.hiddenInLegend = true;
            }
        }

        let projection: LineSeries;
        if (series.useProjection) {
            projection = this.amchart.series.push(new LineSeries());
            dataFields = projection.dataFields;

            dataFields.valueY = 'yValue';
            dataFields.valueX = 'xValue';

            projection.hiddenInLegend = true;
            projection.stroke = am4core.color(series.projectionColor || color);
            projection.strokeWidth = series.projectionStrokeSize || 1;
            projection.strokeOpacity = series.projectionStrokeOpacity || 1;
            projection.strokeDasharray = series.projectionStrokeDashArray || '8,3';
        }

        this.seriesWithProjections.push({dataLine: amseries, projectionLine: projection});
    }


    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);

        this.amchart.scrollbarX.visible = false;
        this.amchart.zoomOutButton.visible = false;
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        super.unsetExportStyles();

        this.amchart.zoomOutButton.visible = true;
        this.amchart.scrollbarX.visible = true;
    }

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        const oldData = this.rawData;
        this.rawData = data;
        if (!data) {
            this.amchart.data = null;

            return;
        }

        // this.amchart.data = JSON.parse(JSON.stringify(data));

        data.forEach((dataList, index) => {
            if (oldData?.[index] === dataList) {
                return;
            }

            const line = this.seriesWithProjections[index];
            const amseries = line.dataLine;
            const projection = line.projectionLine;

            amseries.removeData(amseries.dataItems.length + 1);

            if (!dataList) {
                return;
            }
            amseries.addData(dataList);

            if (!projection) {
                return;
            }

            projection.removeData(2);
            const projectionStart = JSON.parse(JSON.stringify(dataList[dataList.length - 1]));
            const xDiff = projectionStart.xValue - dataList[0].xValue;
            const yDiff = projectionStart.yValue - dataList[0].yValue;
            const projectionRange = this.options.maxXValue - projectionStart.xValue;
            const ratio = yDiff / xDiff;

            const projectionEnd = {
                label: '' + this.options.maxXValue,
                xValue: this.options.maxXValue,
                yValue: projectionStart.yValue + ratio * projectionRange
            };
            projection.addData([
                projectionStart,
                projectionEnd
            ]);
        });

        this.updateSeriesStyles();
        this.ready.next(true);
    }
    /**
     * Required but not currently used
     */
    updateSeriesStyles(): void {}

    /**
     * Exports data using amcharts
     */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {

        return this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return false;
    }
}
